import { useCallback, useEffect, useMemo, useState } from "react";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import { useMemberAccessLogViewStore } from "@hooks/view/useMemberAccessLogViewStore";
import { View } from "@common/types/views/views";
import { OrganizationMemberAccessLogViewSchema } from "@common/types/views/organizationMemberAccessLogView";
import { OrganizationMemberClientAccessSort } from "@common/types/organizationMemberClientAccess";
import { ORGANIZATION_MEMBER_CLIENT_ACCESS_LIST_MAX_LIMIT } from "@common/schemas/organization-member-client-access.schema";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { PaginatedTableFooter } from "@components/organizationView/shared/PaginatedTableFooter";
import { useClampedPageCount } from "@components/organizationView/shared/useClampedPageCount";
import { useOrganizationMemberAccessLog } from "@api/organizationMemberAccessLog.api";
import { memberAccessLogViewConfig } from "./memberAccessLogViewConfig";
import { MemberAccessLogRow } from "./memberAccessLogRow";
import { MemberAccessLogFiltersControls } from "./MemberAccessLogFiltersControls";
import { DEFAULT_MEMBER_ACCESS_LOG_SORT } from "./memberAccessLogSortConfig";
import styles from "./MemberAccessLogPage.module.scss";

const PAGE_SIZE = ORGANIZATION_MEMBER_CLIENT_ACCESS_LIST_MAX_LIMIT;

export const MemberAccessLogPage = () => {
    const organization = useActiveOrganization();
    const organizationId = organization?.id ?? "";
    const { view, setView } = useMemberAccessLogViewStore();

    const [page, setPage] = useState(1);
    const [memberId, setMemberId] = useState<number | undefined>(undefined);
    const [clientWorkspaceId, setClientWorkspaceId] = useState<
        string | undefined
    >(undefined);
    const [sort, setSort] = useState<OrganizationMemberClientAccessSort>(
        DEFAULT_MEMBER_ACCESS_LOG_SORT,
    );

    // Reset to page 1 whenever a filter/sort narrows or reorders the matching
    // set, so the user doesn't land on a now out-of-range page.
    useEffect(() => {
        setPage(1);
    }, [memberId, clientWorkspaceId, sort]);

    const { data, isLoading, isFetching } = useOrganizationMemberAccessLog({
        organizationId,
        query: {
            page,
            limit: PAGE_SIZE,
            memberId,
            clientWorkspaceId,
            sort,
        },
        enabled: !!organizationId,
    });

    const total = data?.total ?? 0;
    const pageCount = useClampedPageCount({
        totalPages: data?.pageCount,
        page,
        setPage,
    });

    const rows: MemberAccessLogRow[] | null = useMemo(() => {
        if (!data) {
            return null;
        }
        return data.data;
    }, [data]);

    const hasActiveFilters =
        memberId !== undefined || clientWorkspaceId !== undefined;

    const clearFilters = useCallback(() => {
        setMemberId(undefined);
        setClientWorkspaceId(undefined);
    }, []);

    const handleSortChange = useCallback(
        (nextSort: OrganizationMemberClientAccessSort) => {
            setSort(nextSort);
        },
        [],
    );

    const handleViewChange = useCallback(
        (nextView: View) => {
            const parsed =
                OrganizationMemberAccessLogViewSchema.safeParse(nextView);
            if (parsed.success) {
                setView(parsed.data);
            }
        },
        [setView],
    );

    const emptyView = useCallback(() => {
        if (hasActiveFilters) {
            return (
                <EmptyState
                    header="No matching access history"
                    action={clearFilters}
                />
            );
        }
        return (
            <EmptyState
                header="No access history yet"
                body="Member access to clients will show up here."
                ctaText={undefined}
            />
        );
    }, [hasActiveFilters, clearFilters]);

    const footer = useMemo(
        () =>
            total > 0 ? (
                <PaginatedTableFooter
                    total={total}
                    pageCount={pageCount}
                    page={page}
                    onPageChange={setPage}
                    itemLabel="access record"
                />
            ) : undefined,
        [total, pageCount, page],
    );

    return (
        <main className={styles.container}>
            <TablePageHeader
                isFullPage
                beforeFiltersChildren={
                    <MemberAccessLogFiltersControls
                        memberId={memberId}
                        clientWorkspaceId={clientWorkspaceId}
                        onMemberIdChange={setMemberId}
                        onClientWorkspaceIdChange={setClientWorkspaceId}
                    />
                }
            />

            <ViewTable
                data={isLoading && !rows ? null : (rows ?? [])}
                view={view}
                config={memberAccessLogViewConfig}
                onViewChange={handleViewChange}
                sort={sort}
                onSortChange={handleSortChange}
                enableColumnResizing
                fixedTopOffsetPx
                emptyView={emptyView}
                footer={footer}
                isSortLoading={isFetching}
            />
        </main>
    );
};
